import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dropdown,
  Field,
  Link,
  Option,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { findStatusOption } from '@moko/utils';
import {
  getTenantIdFromToken,
  isTenantFlagOn,
} from '@moko/utils/src/tenantHelper';
import { Features, useFeatureFlag } from '@moko/utils/src/featureAccess';
import type { WorkComponentProps, IWorkDetailsServices } from './types';
import EditItem, { type IEditItemToolbarButton } from '../EditItem';
import { useTenantWorkTypes } from './useTenantWorkTypes';
import { FIELD_LABEL_COLUMNS } from '../OtherInfo/panelLayout';
import { useTenant } from '../TenantProvider';
import OrderDetailsDrawer, {
  type GetOrderService,
  type GetOrderMetadataService,
} from './OrderDetailsDrawer';

interface WorkDetailsProps extends WorkComponentProps {
  workId: number;
  services: IWorkDetailsServices;
  loadedWork?: Record<string, unknown> | null;
  loadedStatuses?: { id: number; statusId?: number; name: string }[];
  loadedStatusGroups?: { id: number; statusId?: number; name: string }[];
  loadedActionStatuses?: {
    id: number;
    name: string;
    actionLabel?: string | null;
    showAsAction?: boolean;
  }[];
  getOrder?: GetOrderService;
  getOrderMetadata?: GetOrderMetadataService;
  postButtons?: IEditItemToolbarButton[];
  onWorkSaved?: (work: {
    description?: string | null;
    statusId?: number | null;
  }) => void;
  readOnly?: boolean;
  editing?: boolean;
  onEditingChange?: (editing: boolean) => void;
}

const useStyles = makeStyles({
  headerGrid: {
    display: 'grid',
    gridTemplateColumns: FIELD_LABEL_COLUMNS,
    columnGap: '8px',
    rowGap: '12px',
    alignItems: 'center',
  },
  headerLabel: {
    overflowWrap: 'break-word',
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
  headerValue: {
    overflowWrap: 'break-word',
    minWidth: 0,
  },
  workTypeHint: {
    gridColumn: '2',
    marginTop: '-8px',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  instruction: {
    overflowWrap: 'break-word',
    color: tokens.colorPaletteGreenForeground1,
  },
  statusField: {
    gridColumn: '1 / -1',
    '& > .fui-Field__label': {
      marginInline: 0,
      maxWidth: '100%',
      minWidth: 0,
      overflowWrap: 'break-word',
    },
  },
});

const WorkDetails = ({
  workId,
  backPath,
  services,
  loadedWork,
  loadedStatuses,
  loadedStatusGroups,
  loadedActionStatuses,
  getOrder,
  getOrderMetadata,
  postButtons,
  onWorkSaved,
  setSuccessMessage,
  setErrorMessage,
  readOnly = false,
  editing,
  onEditingChange,
}: WorkDetailsProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const styles = useStyles();
  const { tenant } = useTenant();
  const showAssetInCreateWork = isTenantFlagOn(
    tenant?.otherInfo,
    'showAssetInCreateWork'
  );
  const standardInstruction = tenant?.otherInfo?.standardInstruction?.trim();
  const { isEnabled: isAssetsEnabled } = useFeatureFlag(
    Features.FEATURE_ASSETS
  );
  const { isEnabled: isZonesEnabled } = useFeatureFlag(Features.FEATURE_ZONES);
  const isSaved = useRef(false);
  const [workStatuses, setWorkStatuses] = useState<
    { id: number; statusId?: number; name: string }[]
  >([]);
  const [orderId, setOrderId] = useState<number | undefined>();
  const [orderDrawerOpen, setOrderDrawerOpen] = useState(false);
  const [statusId, setStatusId] = useState('');
  const [statusSaving, setStatusSaving] = useState(false);
  const [createWorkTypeId, setCreateWorkTypeId] = useState<number | null>(null);

  const isExistingWork = !!workId && !Number.isNaN(workId);
  const workTenantId =
    typeof loadedWork?.tenantId === 'number'
      ? loadedWork.tenantId
      : getTenantIdFromToken() ?? undefined;
  const [seenWorkTenantId, setSeenWorkTenantId] = useState(workTenantId);
  if (seenWorkTenantId !== workTenantId) {
    setSeenWorkTenantId(workTenantId);
    if (seenWorkTenantId != null && workTenantId != null) {
      setCreateWorkTypeId(null);
    }
  }
  const workTypes = useTenantWorkTypes(
    workTenantId,
    typeof services.getTenantWorkTypes === 'function',
    services.getTenantWorkTypes
  );
  const savedWorkTypeId =
    typeof loadedWork?.workTypeId === 'number' ? loadedWork.workTypeId : null;
  const pageEdit = onEditingChange != null;
  const showStandaloneStatus = pageEdit && isExistingWork;
  const useWorkStatusApi = services.updateWorkStatus != null;
  const statuses = loadedStatuses ?? workStatuses;
  const selectedWorkStatusId = findStatusOption(
    statuses,
    loadedWork?.statusId as number | null | undefined
  )?.statusId;
  const groupStatusOptions = (loadedStatusGroups ?? []).flatMap((group) =>
    typeof group.statusId === 'number'
      ? [{ key: String(group.statusId), name: group.name, groupId: group.id }]
      : []
  );
  const statusOptions = loadedStatusGroups
    ? groupStatusOptions.map((group) => ({
        key: group.groupId,
        id: Number(group.key),
        name: group.name,
      }))
    : useWorkStatusApi
    ? (loadedActionStatuses ?? []).map((item) => ({
        key: item.id,
        id: item.id,
        name: item.actionLabel || item.name,
      }))
    : statuses.map((item) => ({
        key: item.id,
        id: item.id,
        name: item.name,
      }));

  useEffect(() => {
    if (isExistingWork) return;
    const abortController = new AbortController();
    services
      .getWorkStatuses(abortController, getTenantIdFromToken() ?? undefined)
      .then(setWorkStatuses)
      .catch((error: any) => {
        console.error('Error:', error);
        setErrorMessage(error.message);
      });
    return () => abortController.abort();
  }, [isExistingWork]);

  useEffect(() => {
    if (useWorkStatusApi) {
      if (selectedWorkStatusId == null) return;
      setStatusId(String(selectedWorkStatusId));
      return;
    }
    if (loadedWork?.statusId == null) return;
    setStatusId(String(loadedWork.statusId));
  }, [loadedWork?.statusId, selectedWorkStatusId, useWorkStatusApi]);

  const handleStatusSelect = async (next: string) => {
    if (!isExistingWork || next === statusId || statusSaving) return;
    const previous = statusId;
    setStatusId(next);
    setStatusSaving(true);
    try {
      const abortController = new AbortController();
      const description =
        typeof loadedWork?.description === 'string'
          ? loadedWork.description
          : undefined;
      const parsedStatus = Number.parseInt(next, 10);
      if (services.updateWorkStatus) {
        await services.updateWorkStatus(abortController, workId, parsedStatus);
      } else {
        await services.updateWork(abortController, workId, {
          description,
          statusId: parsedStatus,
        });
      }
      onWorkSaved?.({ description, statusId: parsedStatus });
      setSuccessMessage(`Work with id: ${workId} successfully updated.`);
    } catch (error: unknown) {
      setStatusId(previous);
      setErrorMessage(
        (error as { message?: string })?.message ??
          `Failed to update work ${workId}.`
      );
    } finally {
      setStatusSaving(false);
    }
  };

  const orderLink =
    getOrder && orderId !== undefined ? (
      <>
        <span className={styles.headerLabel}>
          {t('workOrderId', 'Work Order ID')}:
        </span>
        <Link onClick={() => setOrderDrawerOpen(true)}>
          {t('order', 'Order')} #{orderId}
        </Link>
      </>
    ) : null;

  const instructionRow = standardInstruction ? (
    <>
      <span className={styles.headerLabel}>
        {t('instruction', 'Instruction')}:
      </span>
      <span className={styles.instruction}>{standardInstruction}</span>
    </>
  ) : null;

  const statusField = showStandaloneStatus ? (
    <Field
      className={styles.statusField}
      label={t('statusId', 'Status Id')}
      orientation="horizontal"
      style={{
        gridColumn: '1 / -1',
        gridTemplateColumns: FIELD_LABEL_COLUMNS,
        columnGap: '8px',
      }}
    >
      <Dropdown
        style={{ minWidth: 'auto', width: '100%' }}
        value={
          statusOptions.find((item) => String(item.id) === statusId)?.name ?? ''
        }
        selectedOptions={statusId ? [statusId] : []}
        disabled={readOnly || statusSaving}
        onOptionSelect={(_, data) => {
          if (data.optionValue) void handleStatusSelect(data.optionValue);
        }}
      >
        {statusOptions.map((item) => (
          <Option key={item.key} value={String(item.id)}>
            {item.name}
          </Option>
        ))}
      </Dropdown>
    </Field>
  ) : null;

  const workTypeId = isExistingWork ? savedWorkTypeId : createWorkTypeId;
  const workTypeName = workTypes.options.find(
    (option) => option.id === workTypeId
  )?.name;
  const workTypeLabel =
    workTypeId == null
      ? t('noWorkType', 'No work type')
      : workTypeName ??
        t('unavailableWorkType', 'Unavailable work type ({{id}})', {
          id: workTypeId,
        });
  const workTypeLoading =
    !!services.getTenantWorkTypes &&
    (workTypes.loading || (isExistingWork && loadedWork == null));
  const workTypeRow = services.getTenantWorkTypes ? (
    <>
      <span className={styles.headerLabel}>{t('workType', 'Work type')}:</span>
      {workTypeLoading ? (
        <Spinner size="tiny" />
      ) : workTypes.error ? (
        <span className={styles.headerValue}>
          {t('workTypesLoadFailed', 'Could not load work types.')}{' '}
          <Button
            appearance="transparent"
            size="small"
            onClick={workTypes.refetch}
          >
            {t('retry', 'Retry')}
          </Button>
        </span>
      ) : isExistingWork ? (
        <span className={styles.headerValue}>{workTypeLabel}</span>
      ) : (
        <>
          <Dropdown
            style={{ minWidth: 'auto', width: '100%' }}
            value={workTypeName ?? t('noWorkType', 'No work type')}
            selectedOptions={[workTypeId == null ? '' : String(workTypeId)]}
            disabled={readOnly}
            onOptionSelect={(_, data) => {
              const next = data.optionValue;
              setCreateWorkTypeId(
                next == null || next === '' ? null : Number(next)
              );
            }}
          >
            <Option value="">{t('noWorkType', 'No work type')}</Option>
            {workTypes.options.map((option) => (
              <Option key={option.id} value={String(option.id)}>
                {option.name}
              </Option>
            ))}
          </Dropdown>
          {workTypes.options.length === 0 && (
            <span className={styles.workTypeHint}>
              {t('noWorkTypes', 'No work types for this tenant.')}
            </span>
          )}
        </>
      )}
    </>
  ) : null;

  const headerSlot =
    workTypeRow || orderLink || instructionRow || statusField ? (
      <div className={styles.headerGrid}>
        {workTypeRow}
        {orderLink}
        {instructionRow}
        {statusField}
      </div>
    ) : undefined;

  return (
    <div
      style={{
        padding: '12px',
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: '4px',
        width: '100%',
        maxWidth: 640,
        boxSizing: 'border-box' as const,
      }}
    >
      <EditItem
        actionsPlacement="bottom"
        hideToolbar={readOnly}
        otherInfoReadOnly={pageEdit ? !editing : readOnly}
        otherInfoCanToggleEdit={pageEdit}
        editing={editing}
        onEditingChange={onEditingChange}
        headerSlot={headerSlot}
        isDirtyExternal={!isExistingWork && createWorkTypeId != null}
        getAction={async (abortController, id) => {
          const work: any = loadedWork
            ? { ...loadedWork }
            : await services.getWork(abortController, id);
          setWorkStatuses(
            loadedStatuses ??
              (await services.getWorkStatuses(
                abortController,
                work?.tenantId ?? getTenantIdFromToken() ?? undefined
              ))
          );
          const rawOrderId = work?.orderId;
          setOrderId(
            typeof rawOrderId === 'number' && rawOrderId > 0
              ? rawOrderId
              : undefined
          );
          const currentStatus = Number(work.statusId);
          const selectedGroup =
            groupStatusOptions.find(
              (group) => Number(group.key) === currentStatus
            ) ??
            groupStatusOptions.find((group) => group.groupId === currentStatus);
          work.statusId = selectedGroup
            ? selectedGroup.key
            : (work.statusId ?? '').toString();
          work.assigneeName = (work.assigneeName ?? '').toString();
          return work;
        }}
        newAction={async (abortController, newWork) => {
          try {
            const res = await services.createWork(abortController, {
              description: newWork.description as string | undefined,
              statusId: newWork.statusId
                ? Number.parseInt(newWork.statusId as string)
                : undefined,
              ...(services.getTenantWorkTypes
                ? { workTypeId: createWorkTypeId }
                : {}),
            });
            onWorkSaved?.({
              description: newWork.description as string | undefined,
              statusId: newWork.statusId
                ? Number.parseInt(newWork.statusId as string)
                : undefined,
            });
            setSuccessMessage(
              `Work with id: ${res.workId} successfully created.`
            );
            navigate(`${backPath}/edit?id=${res.workId}`);
          } catch (error) {
            setErrorMessage(
              (error as { message?: string })?.message ??
                'Failed to create work.'
            );
            throw error;
          }
        }}
        editAction={async (abortController, id, editedWork) => {
          try {
            const description = editedWork.description as string | undefined;
            if (showStandaloneStatus) {
              await services.updateWork(abortController, id, { description });
              onWorkSaved?.({ description });
            } else {
              const nextStatusId = Number.parseInt(
                editedWork.statusId as string
              );
              await services.updateWork(abortController, id, {
                description,
                statusId: nextStatusId,
              });
              onWorkSaved?.({
                description,
                statusId: nextStatusId,
              });
            }
            setSuccessMessage(`Work with id: ${id} successfully updated.`);
          } catch (error: any) {
            console.error('Error:', error);
            setErrorMessage(error?.message ?? `Failed to update work ${id}.`);
          }
        }}
        isSaved={isSaved}
        back={backPath}
        postButtons={postButtons}
        metadata={{
          fields: [
            ...(showStandaloneStatus
              ? []
              : [
                  {
                    name: 'statusId',
                    fieldType: 'Lookup',
                    label: 'Status Id',
                    lookupList: 'Statuses',
                  },
                ]),
            ...(isExistingWork
              ? [
                  {
                    name: 'assigneeName',
                    fieldType: 'Text',
                    label: 'Assignee',
                    placeholder: 'No assigned contractor',
                  },
                ]
              : []),
            {
              name: 'description',
              fieldType: 'String',
              label: 'Description',
              rows: 5,
            },
            ...(isAssetsEnabled && showAssetInCreateWork
              ? [
                  {
                    name: 'assetCodeAndName',
                    fieldType: 'String',
                    label: 'Asset Code and Name',
                    readonly: true,
                  },
                ]
              : []),
            ...(isZonesEnabled
              ? [
                  {
                    name: 'zoneCodeAndName',
                    fieldType: 'String',
                    label: 'Zone Code and Name',
                    readonly: true,
                  },
                ]
              : []),
          ],
          lookups: [
            {
              name: 'Statuses',
              values: groupStatusOptions.length
                ? groupStatusOptions.map((group) => ({
                    key: group.key,
                    value: group.name,
                  }))
                : workStatuses.map((wos) => ({
                    key: (wos.statusId ?? wos.id).toString(),
                    value: wos.name,
                  })),
            },
          ],
          validations: [],
          interactions: [],
        }}
      />
      {getOrder && orderId !== undefined && (
        <OrderDetailsDrawer
          open={orderDrawerOpen}
          onClose={() => setOrderDrawerOpen(false)}
          orderId={orderId}
          getOrder={getOrder}
          getMetadata={getOrderMetadata}
          getTenantWorkTypes={services.getTenantWorkTypes}
          workTypeId={
            typeof loadedWork?.workTypeId === 'number'
              ? loadedWork.workTypeId
              : null
          }
        />
      )}
    </div>
  );
};

export default WorkDetails;
